// src/lib/facture-send-flow.js — l'envoi, PUIS la date de transmission.
// Jamais l'inverse, jamais l'un sans l'autre.
//
// 2026-10-03 : la FACTURE part désormais par le serveur, PDF joint
// (emettreEtEnvoyerFacture / envoyerFactureExistante plus bas, le serveur
// écrit lui-même la preuve). sendThenMarkDelivered ne sert plus qu'à l'AVOIR
// (src/lib/avoir-send-flow.js, EmailJS depuis le navigateur). Historique :
//
// Chantier « L'avoir a un bouton » (défaut 3). Le chemin « Envoyer par Gmail »
// (App.jsx sendFactureEmail) part RÉELLEMENT (EmailJS depuis le navigateur,
// aucun gate) mais ne posait jamais invoices.delivered_at : l'Historique
// affichait « Non envoyé » sur des factures que le propriétaire avait reçues,
// et la suppression les traitait comme jamais transmises (sans confirmation
// renforcée, sans audit). Le marqueur n'était posé QUE par un clic manuel sur
// « envoyé » dans l'Historique — ce qui explique le relevé en base : juin et
// juillet marqués à la main, août oublié.
//
// Fonction PURE (dépendances injectées), testable avec un harnais mutable :
//   - send()                → Promise ; l'envoi réel (EmailJS). S'il rejette,
//                             on RETHROW sans toucher au marqueur : un envoi
//                             qui échoue ne pose pas de date de transmission.
//   - markDelivered(id)     → Promise<{ ok, message }> ; l'action serveur
//                             setInvoiceDelivered (service_role — la RLS n'a
//                             aucune policy UPDATE client, on ne bricole pas).
//                             Sur une pièce déjà numérotée elle ne consomme
//                             rien (RPC idempotente) et pose delivered_at.
//
// Retour : { sent: true, delivered: boolean, message: string|null }.
// `delivered:false` avec `sent:true` = le mail est parti mais le marqueur n'a
// pas pu être posé : l'appelant DOIT le dire (l'écran ne doit pas laisser
// croire « non envoyé » sur un mail réellement parti).

import { validerCourriel, remplirModele, courrielRempliValide } from "./courriel-facture";
// P-2.46 : garde « Assujetti TVA » (une seule porte, partagée avec le serveur).
import { refusEmissionTva } from "./tva-garde";

export async function sendThenMarkDelivered({
  invoiceId,
  send,
  markDelivered,
}) {
  if (!invoiceId) throw new Error("sendThenMarkDelivered : invoiceId requis");
  if (typeof send !== "function" || typeof markDelivered !== "function") {
    throw new Error("sendThenMarkDelivered : send et markDelivered requis");
  }
  // 1. L'envoi. Toute exception remonte telle quelle : rien n'est marqué.
  await send();
  // 2. Le marqueur, SEULEMENT après un envoi réussi.
  let r;
  try {
    r = await markDelivered(invoiceId);
  } catch (e) {
    console.error(
      "[sendThenMarkDelivered] marquage transmis échoué après envoi réussi :",
      e,
    );
    return { sent: true, delivered: false, message: e?.message || String(e) };
  }
  if (!r || !r.ok) {
    return {
      sent: true,
      delivered: false,
      message: (r && r.message) || "setInvoiceDelivered refusé",
    };
  }
  return { sent: true, delivered: true, message: null };
}

// ── La réponse du serveur, en clair (2026-10-03) ──────────────────────────
//
// Depuis la décision « PDF joint » (Dimitri, 03/10), la facture part par le
// serveur (api/facture-envoi-proprio.js → lib/facture-envoi-proprio.js), qui
// envoie PUIS écrit lui-même la preuve d'envoi. L'écran ne fait que lire sa
// réponse — et ne dit « envoyée » que si le serveur l'a dit.
//
//   envoye             : parti, preuve enregistrée → « Envoyé » ;
//   envoye_sans_preuve : parti, preuve NON enregistrée → le dire, et dire de
//                        ne PAS renvoyer (le propriétaire l'a reçue) ;
//   simulation         : MESSAGES_DRY_RUN actif, rien n'est parti ;
//   refuse             : refus nommé du serveur, rien n'est parti ;
//   essai              : l'ESSAI est parti à l'admin connecté seul (rien noté) ;
//   incertain          : réponse perdue (réseau, réponse illisible, erreur
//                        serveur sans refus nommé) — le courriel est PEUT-ÊTRE
//                        parti : relire l'Historique, jamais « non envoyé ».
//
// @param {{ httpOk?: boolean, json?: object|null, erreurReseau?: any }} r
// @returns {{ etat: string, message: string|null }}
// Codes où le serveur ne sait pas si le courriel est parti
// (lib/facture-envoi-proprio.js, api/facture-envoi-proprio.js).
const CODES_INCERTAINS = new Set(["erreur_serveur", "envoi_incertain"]);

export function interpreterReponseEnvoi({ httpOk = false, json = null, erreurReseau = null } = {}) {
  if (erreurReseau || !json || typeof json !== "object")
    return { etat: "incertain", message: null };
  if (httpOk && json.ok === true) {
    // ESSAI à l'admin seul : parti chez lui, jamais chez le propriétaire.
    if (json.essai === true)
      return json.sent === true
        ? { etat: "essai", message: null, destinataire: json.destinataire || null }
        : { etat: "incertain", message: null };
    if (json.simulation === true && json.sent !== true)
      return { etat: "simulation", message: json.message || null };
    // L'adresse (masquée) est celle que le SERVEUR a utilisée (point C).
    const destinataire = json.destinataire || null;
    if (json.sent === true && json.delivered === true)
      return { etat: "envoye", message: null, destinataire };
    if (json.sent === true)
      return { etat: "envoye_sans_preuve", message: json.message || null, destinataire };
    return { etat: "incertain", message: null };
  }
  // Refus NOMMÉ (code présent) : le serveur garantit que rien n'est parti —
  // sauf les deux codes où lui-même ne sait pas.
  if (json.ok === false && typeof json.code === "string" && !CODES_INCERTAINS.has(json.code))
    return { etat: "refuse", message: json.error || "Envoi refusé." };
  return { etat: "incertain", message: json.error || null };
}

// ── Émettre ET envoyer, après relecture du courriel ───────────────────────
//
// Le geste « Envoyer au propriétaire » de l'onglet Facturation, APRÈS la
// fenêtre de relecture. L'ordre est la garantie, d'où une fonction pure
// (dépendances injectées) plutôt que du code inline dans l'écran.
//
// P-2.46 bis (30/10) : LE NUMÉRO EST TIRÉ PAR LE SERVEUR. Le navigateur ne
// tire plus aucun numéro (l'ancien contrat tirerNumero + creerPiece, qui
// appelait next_invoice_number depuis le navigateur, est RETIRÉ ; le passer
// lève une erreur avant tout acte) :
//   0. P-2.46 : « Assujetti TVA » coché (ou réglages absents) à l'écran ⇒
//      refus immédiat. Le garde qui fait foi est celui du serveur, en 4 ;
//   1. le courriel est VALIDÉ (objet/texte non vides, aucune variable
//      inconnue, texte rempli sans accolade) ;
//   2. le montant relu est encore celui de la facture (verifierMontant) ;
//   3. creerBrouillon()          → INSERT d'un BROUILLON (aucun numéro,
//                                  HTML figé avec les jetons __INVOICE_*__) ;
//   4. emettreServeur(invoiceId) → l'action serveur de l'« Émettre » d'un
//                                  brouillon (setInvoiceDelivered,
//                                  emettreSeulement) : elle RELIT les
//                                  réglages en base, applique
//                                  refusEmissionTva AVANT la RPC, puis
//                                  assign_invoice_number tire le numéro et
//                                  remplace les jetons dans la MÊME
//                                  transaction. Rejouée sur le même
//                                  brouillon, elle rend le même numéro sans
//                                  en consommer un autre (clé = invoiceId) ;
//   5. les variables sont remplies avec le VRAI numéro et le vrai montant,
//      puis le serveur envoie le courriel relu, PDF joint, PUIS écrit la
//      preuve (envoyerServeur → interpreterReponseEnvoi).
// `html` rendu vaut null : le document émis n'existe qu'en base ;
// l'Historique l'ouvre par son identifiant (src/lib/facture-snapshot.js),
// jamais le brouillon local à jetons.
// Un refus du serveur en 4 : AUCUN courriel ne part ; la pièce préparée reste
// dans l'Historique (`brouillonCree: true` : l'appelant bloque le mois et relit
// la liste, src/lib/facture-envoi-ecran.js). Deux cas distincts
// (lireRefusEmission) : vrai refus (le serveur garantit : aucun numéro) ou
// réponse perdue (`incertain: true` : la facture est peut-être émise).
//
// Retour : { ok:false, raison } (refus avant le brouillon, rien n'a bougé),
//          { ok:false, raison, brouillonCree:true, incertain } (refus serveur), ou
//          { ok:true, numero, inv, html:null, montantTotal, objet, texte, envoi }.
export async function emettreEtEnvoyerFacture(args) {
  if (args && (args.tirerNumero !== undefined || args.creerPiece !== undefined))
    throw new Error(
      "emettreEtEnvoyerFacture : l'ancien contrat (tirerNumero/creerPiece, numéro tiré par le navigateur) est retiré — passer creerBrouillon + emettreServeur",
    );
  return emettreParLeServeur(args || {});
}

// Les refus d'avant le brouillon (ordre inchangé depuis le 03/10) :
// garde TVA, courriel, texte rempli, montant relu. Rend null ou { ok:false, raison }.
async function refusAvantPiece({ courriel, valeurs, verifierMontant, reglages }) {
  // 0. Facture de la conciergerie (série P) : le garde TVA, avant tout le reste.
  const refusTva = refusEmissionTva(reglages, { emetteur: "fbz" });
  if (refusTva) return { ok: false, raison: refusTva.message };
  const verdict = validerCourriel(courriel || {});
  if (!verdict.ok) return { ok: false, raison: verdict.raison };
  const rempli = courrielRempliValide(courriel, valeurs);
  if (!rempli.ok) return { ok: false, raison: rempli.raison };
  const montant = await verifierMontant();
  if (!montant || montant.ok !== true)
    return { ok: false, raison: (montant && montant.raison) || "Montant non vérifié : rien n'est parti." };
  return null;
}

const JETON_NUMERO = "__INVOICE_NUMBER__";

// ── Refus du serveur à l'émission : vrai refus ou réponse perdue ──────────
//
// `emis` = la réponse de l'action setInvoiceDelivered (emettreSeulement),
// avec son code HTTP dans `status` (callSetInvoiceDelivered).
//   · VRAI REFUS : 4xx avec un code nommé (409 « Assujetti TVA », brouillon
//     non attribuable, 404…), ou les deux 500 du garde TVA posés AVANT la RPC
//     (réglages illisibles, pièce non lisible). Le serveur n'a tiré aucun
//     numéro : la pièce reste préparée, SANS numéro.
//   · RÉPONSE PERDUE : tout le reste — 5xx (504 d'une fonction trop lente),
//     réponse illisible, code absent, « ok » sans numéro. Le numéro est
//     PEUT-ÊTRE posé : on ne le dit ni posé ni absent, on renvoie à l'Historique.
//     Même chose pour un « ok » dont la réponse est illisible (callSetInvoiceDelivered
//     lit le corps avec .catch(() => ({}))) : « peut-être émise », jamais « pas de numéro ».
const CODES_REFUS_AVANT_NUMERO_5XX = new Set(["tva_reglages_illisibles", "tva_check_failed"]);
export const APRES_VRAI_REFUS =
  " Aucun courriel n'est parti. La facture préparée reste dans l'Historique, sans numéro : supprime-la ou émets-la depuis l'Historique.";
export const APRES_REPONSE_PERDUE =
  " Aucun courriel n'est parti. La facture est peut-être émise : regarde l'Historique (recharge la page) avant de recommencer.";

export function lireRefusEmission(emis) {
  const status = emis && Number.isInteger(emis.status) ? emis.status : null;
  const code = emis && typeof emis.error === "string" ? emis.error : null;
  const vraiRefus =
    !!code &&
    status !== null &&
    ((status >= 400 && status < 500) || (status >= 500 && CODES_REFUS_AVANT_NUMERO_5XX.has(code)));
  if (vraiRefus) {
    const motif = (typeof emis.message === "string" && emis.message) || "Émission refusée par le serveur.";
    return { incertain: false, raison: motif + APRES_VRAI_REFUS };
  }
  return { incertain: true, raison: "Réponse du serveur perdue." + APRES_REPONSE_PERDUE };
}

async function emettreParLeServeur({
  destinataireAffiche,
  courriel,
  valeurs = {},
  formaterMontant = (n) => String(n),
  creerBrouillon,
  emettreServeur,
  envoyerServeur,
  verifierMontant,
  reglages,
}) {
  for (const [nom, fn] of Object.entries({ creerBrouillon, emettreServeur, envoyerServeur, verifierMontant })) {
    if (typeof fn !== "function")
      throw new Error(`emettreEtEnvoyerFacture : ${nom} requis`);
  }
  // 0 à 2 : le garde TVA côté écran reste en tête (réponse immédiate) ; le
  // garde qui fait foi est celui du serveur, en 4.
  const refus = await refusAvantPiece({ courriel, valeurs, verifierMontant, reglages });
  if (refus) return refus;

  // 3. Le brouillon : aucun numéro consommé.
  const { inv, montantTotal } = await creerBrouillon();
  if (!inv || !inv.id) throw new Error("emettreEtEnvoyerFacture : brouillon créé sans identifiant");

  // 4. Le serveur émet (garde TVA sur les réglages en base, puis numéro).
  //    Une exception (réseau) remonte : on ne sait pas si le numéro est posé,
  //    l'appelant renvoie à l'Historique.
  const emis = await emettreServeur(inv.id);
  if (!emis || emis.ok !== true) {
    const { incertain, raison } = lireRefusEmission(emis);
    return { ok: false, raison, brouillonCree: true, incertain };
  }
  const numero = typeof emis.invoice_number === "string" ? emis.invoice_number.trim() : "";
  if (!numero || numero.includes(JETON_NUMERO)) {
    return {
      ok: false,
      raison: "Réponse du serveur illisible." + APRES_REPONSE_PERDUE,
      brouillonCree: true,
      incertain: true,
    };
  }

  // 5. Variables avec le VRAI numéro, puis envoi par le serveur (inchangé).
  const pleines = { ...valeurs, numero, montant: formaterMontant(montantTotal) };
  const objet = remplirModele(courriel.objet, pleines);
  const texte = remplirModele(courriel.texte, pleines);
  const envoi = await envoyerServeur({ invoiceId: inv.id, objet, texte, destinataireAffiche });
  // html: null — le document émis se lit en base (voir l'en-tête).
  return { ok: true, numero, inv, html: null, montantTotal, objet, texte, envoi };
}

// ── Envoyer une facture DÉJÀ émise ────────────────────────────────────────
//
// Bouton « Envoyer » d'une facture numérotée qui n'est pas partie. Même
// fenêtre de relecture, mais AUCUN numéro tiré, AUCUNE pièce créée — il n'y a
// pas de tirerNumero ni de creerPiece dans cette fonction, par construction.
// Le PDF joint est fabriqué par le serveur à partir du document FIGÉ de la
// pièce (html_snapshot, CGI 289), jamais régénéré.
//
// Retour : { ok:false, raison } (refus AVANT tout envoi) ou
//          { ok:true, objet, texte, envoi }.
export async function envoyerFactureExistante({
  invoiceId,
  numero,
  courriel,
  valeurs = {},
  envoyerServeur,
  essai = false,
  destinataireAffiche,
}) {
  if (typeof envoyerServeur !== "function")
    throw new Error("envoyerFactureExistante : envoyerServeur requis");
  if (!invoiceId || !numero)
    return { ok: false, raison: "Facture sans numéro : émets-la d'abord." };
  const verdict = validerCourriel(courriel || {});
  if (!verdict.ok) return { ok: false, raison: verdict.raison };
  const rempli = courrielRempliValide(courriel, valeurs);
  if (!rempli.ok) return { ok: false, raison: rempli.raison };

  const pleines = { ...valeurs, numero };
  const objet = remplirModele(courriel.objet, pleines);
  const texte = remplirModele(courriel.texte, pleines);
  // `essai` : le serveur force le destinataire à l'admin connecté et n'écrit
  // rien (lib/facture-envoi-proprio.js, envoyerEssai).
  const envoi = await envoyerServeur({ invoiceId, objet, texte, essai: essai === true, destinataireAffiche });
  return { ok: true, objet, texte, envoi };
}
